<script setup lang="ts">
import { onMounted, computed, ref } from "vue";
import { bemBuilder } from "@chatfood/core-utils";
import { AtomText, SatIconButton } from "@chatfood/design-system";
import { useLocale } from "~/composables/use-locale";
import { useBusinessStore } from "~/stores/business";
import { useCustomerStore } from "~/stores/customer";
import { useNavigation } from "~/composables/use-navigation";
import { useDate } from "~/composables/use-date";
import { useBugReporter, useDirectBackend } from "#imports";

const business = useBusinessStore();
const customer = useCustomerStore();

const { resolvePath } = useNavigation();
const { report } = useBugReporter();
const { isOlderThan, formatDatetime, getTimeFormat } = useDate();
const { t } = useLocale();
const { orderRepo } = useDirectBackend();

const css = bemBuilder("order-notifier");

type OngoingOrder = Awaited<
  ReturnType<(typeof orderRepo)["getOngoingOrders"]>
>["data"];

const ongoingOrder = ref<OngoingOrder[0]>();

const fetchOngoingOrder = async () => {
  let response: OngoingOrder = [];

  try {
    const { data } = await orderRepo.getOngoingOrders({
      businessId: business.id,
    });

    if (data.length) {
      response = data;
    }

    response?.sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt));

    const order = Array.isArray(response) && response[0];
    if (!order || isOlderThan(order.createdAt, 2, "hours")) return;

    ongoingOrder.value = order;
  } catch (e: unknown) {
    report(e);
  }
};

const cardDescription = computed(() => {
  if (!ongoingOrder.value) {
    return "";
  }

  return t("components.order_notifier.details", {
    orderId: ongoingOrder.value.uid,
    date: formatDatetime(
      ongoingOrder.value.createdAt,
      `D MMM YYYY [at] ${getTimeFormat()}`,
    ),
  });
});

onMounted(async () => {
  if (!customer.isLogged) return;
  await fetchOngoingOrder();
});
</script>

<template>
  <NuxtLink
    v-if="customer.isLogged && ongoingOrder"
    :class="css('')"
    :to="resolvePath('order-tracking', { trackingId: ongoingOrder.trackingId })"
  >
    <div :class="css('content')">
      <AtomText color="color-on-primary" type="subtitle">
        {{ t("components.order_notifier.title") }}
      </AtomText>
      <AtomText type="body" color="color-on-primary">
        {{ cardDescription }}
      </AtomText>
    </div>
    <div :class="css('icon')">
      <SatIconButton
        theme="filled"
        size="extra-small"
        icon-name="caret-right-alt"
      />
    </div>
  </NuxtLink>
</template>

<style lang="scss" scoped>
.order-notifier {
  max-width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  background-color: var(--color-primary);
  padding: var(--spacing-md) var(--spacing-lg);
  box-shadow: var(--elevation-level-2);
  border-radius: var(--radii-card-md);
  gap: var(--spacing-md);
  cursor: pointer;

  &__content {
    text-align: left;
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
  }
}
</style>
